import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { inject } from '@angular/core';
import { debounce, distinctUntilChanged, of, Subject, Subscription, switchMap, tap, timer } from 'rxjs';
import { ILinkPreview, LinkPreviewService } from '../../../shared/services/link-preview.service';

/**
 * Facebook-style link preview card.
 *
 * Give it the caption text; it finds the first URL, pulls that page's Open Graph
 * data (falling back to plain HTML metadata) and renders a card. Renders nothing
 * when the text has no link or the link has no usable metadata.
 *
 * Only ever mounted for posts that have images/videos — background/pattern posts
 * deliberately don't get link previews, since the caption *is* the artwork.
 */
@Component({
  selector: 'app-link-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './link-preview.html',
})
export class LinkPreviewComponent implements OnChanges, OnDestroy {

  /** Caption / description to scan for a link. */
  @Input() text = '';
  /** True when `text` is rich-text HTML (event and blog description editors). */
  @Input() isHtml = false;
  /** Set false to suppress the card entirely (e.g. a background post). */
  @Input() enabled = true;
  /** `plain` renders nothing but the card itself. `composer` adds the loading
   *  skeleton and "Link preview" hint used by the post modal's caption box. */
  @Input() variant: 'composer' | 'plain' = 'plain';
  /** Typing delay before scraping. Kept short for pastes, long enough to type. */
  @Input() debounceMs = 600;

  readonly preview = signal<ILinkPreview | null>(null);
  readonly loading = signal(false);
  readonly imageFailed = signal(false);

  private linkPreviewService = inject(LinkPreviewService);
  private url$ = new Subject<string | null>();
  private sub: Subscription;

  constructor() {
    this.sub = this.url$
      .pipe(
        distinctUntilChanged(),
        tap(url => {
          this.preview.set(null);
          this.imageFailed.set(false);
          this.loading.set(!!url);
        }),
        // Read through `this` on every emission — @Input values aren't bound yet
        // when the constructor builds this pipeline.
        debounce(() => timer(this.debounceMs)),
        switchMap(url => (url ? this.linkPreviewService.get(url) : of(null))),
      )
      .subscribe(result => {
        this.preview.set(result);
        this.loading.set(false);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['text'] && !changes['enabled']) return;
    this.url$.next(this.enabled ? this.currentUrl() : null);
  }

  private currentUrl(): string | null {
    return this.isHtml
      ? this.linkPreviewService.extractFirstUrlFromHtml(this.text)
      : this.linkPreviewService.extractFirstUrl(this.text);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.url$.complete();
  }

  /** Hostname shown in the card's top line, e.g. "bbc.co.uk". */
  get displayDomain(): string {
    const data = this.preview();
    return (data?.siteName || data?.domain || '').toUpperCase();
  }

  onImageError(): void { this.imageFailed.set(true); }

  /** The card sits inside clickable post cards — don't trigger their navigation. */
  onCardClick(event: MouseEvent): void { event.stopPropagation(); }
}
